import { memo, type PropsWithChildren, type RefObject } from 'react';

import { type GallerySection, GalleryView, type GalleryViewProps } from '@air/component-gallery-view';

import { type GalleryMetadata } from '~/components/Gallery/galleryMetadata';
import { SelectedItemsMouseSelectionContainer } from '~/components/SelectedItemsMouseSelectionContainer';
import { useConfirmPreSelectedItemOnMouseUp } from '~/hooks/useConfirmPreSelectedItemOnMouseUp';
import { useGallerySelectableItemsPositions } from '~/hooks/useGallerySelectableItemsPositions';
import { type UseSelectedItemsMouseSelectionParams } from '~/hooks/useSelectedItemsMouseSelection';

export type SelectableGalleryViewProps = {
  containerRef: RefObject<HTMLDivElement>;
  sections: GallerySection<GalleryMetadata>[];
  minSelectionTopPosition?: UseSelectedItemsMouseSelectionParams['minSelectionTopPosition'];
} & GalleryViewProps;

export const SelectableGalleryView = memo(
  ({
    containerRef,
    sections,
    scrollElementRef,
    minSelectionTopPosition = 110,
    ...galleryViewProps
  }: PropsWithChildren<SelectableGalleryViewProps>) => {
    const { itemsPositions } = useGallerySelectableItemsPositions({
      sections,
    });

    // Confirm selection from a stable owner that outlives card remounts (see hook docs).
    useConfirmPreSelectedItemOnMouseUp();

    return (
      <SelectedItemsMouseSelectionContainer
        scrollElementRef={scrollElementRef}
        containerRef={containerRef}
        itemsPositions={itemsPositions}
        minSelectionTopPosition={minSelectionTopPosition}
      >
        <GalleryView scrollElementRef={scrollElementRef} sections={sections} {...galleryViewProps} />
      </SelectedItemsMouseSelectionContainer>
    );
  },
);

SelectableGalleryView.displayName = 'SelectableGalleryView';
